// src/components/Dialogs/ConfirmIconReplaceDialog.tsx
import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, X } from 'lucide-react';

interface ConfirmIconReplaceDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  oldIconName: string;
  newIconName: string;
  oldIconUrl?: string;
  newIconUrl?: string;
}

export const ConfirmIconReplaceDialog: React.FC<ConfirmIconReplaceDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  oldIconName,
  newIconName,
  oldIconUrl,
  newIconUrl
}) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md animate-in fade-in zoom-in-95">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl border border-gray-200 dark:border-slate-700">
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                    Replace Icon?
                  </Dialog.Title>
                  <Dialog.Description className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    This will replace the existing icon
                  </Dialog.Description>
                </div>
              </div>
              <Dialog.Close asChild>
                <button
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-slate-700 dark:hover:text-gray-300 transition-colors"
                  onClick={onCancel}
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Before & After Preview */}
              <div className="flex items-center justify-center gap-4">
                {/* Old Icon */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="relative">
                    {oldIconUrl ? (
                      <img
                        src={oldIconUrl}
                        alt={oldIconName}
                        className="h-16 w-16 rounded-lg border-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 object-contain p-2"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-lg border-2 border-gray-300 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                        <span className="text-2xl">📦</span>
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      ✕
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Current
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white max-w-[100px] truncate">
                    {oldIconName}
                  </span>
                </div>

                {/* Arrow */}
                <div className="flex items-center">
                  <svg
                    className="h-8 w-8 text-gray-400 dark:text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </div>

                {/* New Icon */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="relative">
                    {newIconUrl ? (
                      <img
                        src={newIconUrl}
                        alt={newIconName}
                        className="h-16 w-16 rounded-lg border-2 border-green-500 dark:border-green-400 bg-white dark:bg-slate-900 object-contain p-2 shadow-lg"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-lg border-2 border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                        <span className="text-2xl">📦</span>
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-green-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      ✓
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    New
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white max-w-[100px] truncate">
                    {newIconName}
                  </span>
                </div>
              </div>

              {/* Info Message */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Note:</strong> All connections, position, and properties will be preserved.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 rounded-b-lg">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md transition-colors shadow-sm"
              >
                Replace Icon
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};